import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useRef
} from "react";
import {
	RichTextEditorContent,
	RichTextEditorContentHandle
} from "client/Components";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { CommunityPostComposerPlaceholder } from "./CommunityPostComposerPlaceholder";
import { ensureNotNil } from "client/Misc/utils";
import styles from "./CommunityPostComposerMessage.module.less";
import { userMentionType } from "client/Components/MentionInput/converters";
import { generateUserDetailsUrl } from "../../Builder/misc";
import { DropOverMentionSuggestionBox } from "../../MentionSuggestionBox/DropOverMentionSuggestionBox";
import { findUsers } from "client/Api/Hub/hub";
import { RichTextEditorState } from "client/Components/RichTextEditor/types";

export type CommunityPostComposerMessageHandle = {
	focus: () => void;
};

export type CommunityPostComposerMessageProps = {
	value: RichTextEditorState;
	onChange: (newValue: RichTextEditorState, isSignificant: boolean) => void;
	onBlur: (newValue: RichTextEditorState) => void;
	onFocus: () => void;
};

export const CommunityPostComposerMessage = forwardRef(
	(
		props: CommunityPostComposerMessageProps,
		ref: Ref<CommunityPostComposerMessageHandle>
	): ReactElement => {
		const {
			onChange,
			onBlur,
			onFocus,
			value
		} = props;

		const contentRef: RefObject<RichTextEditorContentHandle | null> = useRef<RichTextEditorContentHandle>(null);
		const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

		const findUsersHandler = useCallback(
			(
				searchTerm: string,
				amount: number,
				signal: AbortSignal
			): Promise<readonly UserIdentityModel[]> => {
				return findUsers(
					searchTerm,
					amount,
					signal
				);
			},
			[]
		);

		useImperativeHandle(
			ref,
			(): CommunityPostComposerMessageHandle => ({
				focus(): void {
					contentRef.current
						?.focus();
				}
			}),
			[]
		);

		return (
			<div
				className={styles.root}
				ref={containerRef}
			>
				<RichTextEditorContent
					onChange={onChange}
					value={value}
					onBlur={onBlur}
					onFocus={() => onFocus()}
					trackingId="editor"
					height="fill"
					ref={contentRef}
					placeholder={<CommunityPostComposerPlaceholder />}
					isBorderless
					hasNoPadding
					mentionTypes={{
						[userMentionType]: {
							kind: "insert-anchor",
							hrefPattern: generateUserDetailsUrl
						}
					}}
					renderSuggestionBox={
						(props, ref, rect) => (
							<DropOverMentionSuggestionBox
								{...props}
								ref={ref}
								target={ensureNotNil(containerRef.current)}
								findUsers={findUsersHandler}
								rect={rect}
							/>
						)
					}
				/>
			</div>
		);
	}
);
CommunityPostComposerMessage.displayName="CommunityPostComposerMessage";